/**
 * ProjectsWidget — "Mijn projecten". Toont actieve projecten waar de gebruiker
 * aan gekoppeld is (PL/creative/camera/animatie), of alle actieve projecten.
 * Databron: GET /Api/projects (Airtable, alleen actieve statussen).
 *
 * Instellingen: alleenMijn (default aan).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T } from './widgetTokens';

// Airtable lookup-velden komen als array terug — altijd normaliseren (recurring bug).
const asStr = (v) => (Array.isArray(v) ? v.join(', ') : String(v ?? ''));

const STATUS_STYLE = {
  'in progress': { bg: '#ecfdf5', color: T.emerald },
  'on hold': { bg: '#fef3c7', color: '#b45309' },
  'lead': { bg: '#e0e7ff', color: '#4f46e5' },
  'offerte': { bg: '#e0e7ff', color: '#4f46e5' },
};

export function ProjectsSettings({ settings, onChange }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.85rem', color: T.text, mb: 0.5 }}>
        Projecten-instellingen
      </Typography>
      <FormControlLabel
        sx={{ m: 0, display: 'flex', justifyContent: 'space-between', '& .MuiFormControlLabel-label': { fontFamily: T.fontBody, fontSize: '0.8rem', color: T.text } }}
        labelPlacement="start"
        control={<Switch size="small" checked={settings.alleenMijn !== false} onChange={(e) => onChange({ alleenMijn: e.target.checked })} />}
        label="Alleen mijn projecten"
      />
    </Box>
  );
}

export default function ProjectsWidget({ settings = {} }) {
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';
  const [projects, setProjects] = useState(null); // null = laden
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    authFetch(`${BACKEND_URL}/Api/projects`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('fetch failed'))))
      .then((d) => { if (active) setProjects(d.projects || []); })
      .catch(() => { if (active) { setError(true); setProjects([]); } });
    return () => { active = false; };
  }, []);

  const voornaam = (userEmail.split('@')[0].split('.')[0] || '').toLowerCase();

  const shown = useMemo(() => {
    const all = projects || [];
    const alleenMijn = settings.alleenMijn !== false;
    const isMine = (f) => {
      if (!voornaam) return false;
      const namen = [
        asStr(f['Projectleider (Volledige naam)']), asStr(f['Projectleider (Voornaam)']),
        asStr(f['Naam (from Creative)']), asStr(f['Naam (from Cameraman)']), asStr(f['Naam (from Animator)']),
      ].join(', ').toLowerCase();
      return namen.includes(voornaam);
    };
    const list = alleenMijn ? all.filter((p) => isMine(p.fields || {})) : all;
    return list.slice(0, 8);
  }, [projects, settings.alleenMijn, voornaam]);

  if (projects === null) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={18} sx={{ color: T.outlineVariant }} /></Box>;
  }
  if (error) {
    return <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>Projecten konden niet geladen worden.</Typography>;
  }
  if (shown.length === 0) {
    return (
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>
        {settings.alleenMijn !== false ? 'Geen actieve projecten op jouw naam gevonden.' : 'Geen actieve projecten.'}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {shown.map((p) => {
        const f = p.fields || {};
        const status = asStr(f.Status);
        const st = STATUS_STYLE[status.toLowerCase()] || { bg: T.surfaceLow, color: T.secondary };
        return (
          <Box key={p.id} component="a" href="/projects" sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none',
            bgcolor: T.surfaceLow, borderRadius: '0.5rem', p: 1.25, borderLeft: `3px solid ${T.primary}`,
            transition: 'background 0.15s', '&:hover': { bgcolor: T.surfaceHigh },
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, color: T.text }}>
                {f.Projectnummer ? `${asStr(f.Projectnummer)} · ` : ''}{asStr(f.Projectnaam) || 'Naamloos project'}
              </Typography>
              <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mt: 0.15 }}>
                {[asStr(f.KlantnaamString), asStr(f['Projectleider (Voornaam)'])].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            {status && (
              <Typography sx={{
                fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700, flexShrink: 0,
                bgcolor: st.bg, color: st.color, px: 1, py: 0.3, borderRadius: '9999px',
              }}>
                {status}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
